/* CE course finder.
 *
 * Reads the course and bundle catalogue from #ce-finder-data (generated for the
 * prototype, metafield-driven later), asks which bodies the rep reports to and
 * how many hours they still need, and ranks bundles plus add-on courses by the
 * lowest price that covers everything.
 */
(() => {
  'use strict';

  // Regulators the picker understands. `source` names the credit field on a course;
  // `cats` are the sub-requirements the rep can edit, with the full-cycle default.
  const BODIES = [
    { key: 'cfp', group: 'designations', source: 'fp', label: { en: 'CFP (FP Canada)', fr: 'CFP (FP Canada)' },
      cats: [
        { key: 'fp', label: { en: 'Financial Planning', fr: 'Planification financière' }, def: 12 },
        { key: 'pr', label: { en: 'Professional Responsibility', fr: 'Responsabilité professionnelle' }, def: 2 },
        { key: 'total', label: { en: 'Total hours', fr: 'Total des heures' }, def: 25 } ],
      note: { en: '25 per calendar year, at least 12 Financial Planning and 2 Professional Responsibility.', fr: '25 par année civile, dont au moins 12 en planification financière et 2 en responsabilité professionnelle.' } },
    { key: 'qafp', group: 'designations', source: 'fp', label: { en: 'QAFP (FP Canada)', fr: 'QAFP (FP Canada)' },
      cats: [
        { key: 'fp', label: { en: 'Financial Planning', fr: 'Planification financière' }, def: 7 },
        { key: 'pr', label: { en: 'Professional Responsibility', fr: 'Responsabilité professionnelle' }, def: 1 },
        { key: 'total', label: { en: 'Total hours', fr: 'Total des heures' }, def: 12 } ],
      note: { en: '12 per calendar year, at least 7 Financial Planning and 1 Professional Responsibility.', fr: '12 par année civile, dont au moins 7 en planification financière et 1 en responsabilité professionnelle.' } },
    { key: 'clu', group: 'designations', source: 'institute', label: { en: 'CLU (The Institute)', fr: "CLU (L'Institut)" },
      cats: [
        { key: 'total', label: { en: 'Total hours', fr: 'Total des heures' }, def: 30 },
        { key: 'ethics', label: { en: 'of which ethics', fr: 'dont éthique' }, def: 2 } ],
      note: { en: '30 per calendar year, at least 15 accredited by The Institute and 2 from a recognized ethics program. Covers CHS too if you hold both.', fr: '30 par année civile, dont au moins 15 reconnues par l\'Institut et 2 en éthique. Couvre aussi le CHS si vous détenez les deux.' } },
    { key: 'chs', group: 'designations', source: 'institute', label: { en: 'CHS (The Institute)', fr: "CHS (L'Institut)" },
      cats: [
        { key: 'total', label: { en: 'Total hours', fr: 'Total des heures' }, def: 10 },
        { key: 'ethics', label: { en: 'of which ethics', fr: 'dont éthique' }, def: 1 } ],
      note: { en: '10 per calendar year accredited by The Institute, at least 1 from a recognized ethics program.', fr: '10 par année civile reconnues par l\'Institut, dont au moins 1 en éthique.' } },
    { key: 'pfa', group: 'designations', source: 'institute', label: { en: 'PFA (The Institute)', fr: "PFA (L'Institut)" },
      cats: [
        { key: 'total', label: { en: 'Total hours', fr: 'Total des heures' }, def: 20 },
        { key: 'ethics', label: { en: 'of which ethics', fr: 'dont éthique' }, def: 1 } ],
      note: { en: '20 per calendar year, at least 10 accredited by The Institute and 1 from a recognized ethics program.', fr: '20 par année civile, dont au moins 10 reconnues par l\'Institut et 1 en éthique.' } },
    { key: 'iqpf', group: 'designations', source: 'iqpf', label: { en: 'Pl. Fin. (Institut de planification financière)', fr: 'Pl. Fin. (Institut de planification financière)' },
      cats: [
        { key: 'sfpa', label: { en: 'SFPA', fr: 'PDOM' }, def: 15 },
        { key: 'sc', label: { en: 'SC', fr: 'NP' }, def: 10 } ],
      note: { en: '40 PDUs per two-year cycle: 15 IPFP (not offered here), 15 SFPA, 10 SC.', fr: '40 UFC par cycle de deux ans : 15 PFIP (non offert ici), 15 PDOM, 10 NP.' } },
    { key: 'idd', group: 'investment', source: 'idd', label: { en: 'CIRO IDD (investment dealer)', fr: 'OCRI CEP (courtier en placement)' },
      cats: [
        { key: 'pd', label: { en: 'Professional Development', fr: 'Perfectionnement professionnel' }, def: 20 },
        { key: 'compliance', label: { en: 'Compliance', fr: 'Conformité' }, def: 10 } ],
      note: { en: '30 per two-year cycle ending 31 Dec 2027: 20 PD and 10 compliance, with 3 compliance in 2026.', fr: '30 par cycle de deux ans se terminant le 31 déc. 2027 : 20 PP et 10 conformité, dont 3 en 2026.' } },
    { key: 'mfd', group: 'investment', source: 'mfd', label: { en: 'CIRO MFD (mutual fund dealer)', fr: 'OCRI CEC (épargne collective)' },
      cats: [
        { key: 'pd', label: { en: 'Professional Development', fr: 'Perfectionnement professionnel' }, def: 20 },
        { key: 'bc', label: { en: 'Business Conduct', fr: 'Conduite des affaires' }, def: 8 } ],
      note: { en: '30 per cycle ending 30 Nov 2027: 20 PD, 8 Business Conduct, plus 2 MFD compliance from CIRO only.', fr: '30 par cycle se terminant le 30 nov. 2027 : 20 PP, 8 conduite des affaires, plus 2 conformité offerts par l\'OCRI seulement.' } },
    { key: 'aic_life', group: 'insurance', source: 'aic_life', label: { en: 'Alberta (AIC) Life', fr: 'Alberta (AIC) Vie' },
      cats: [ { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 15 } ],
      note: { en: '15 per year ending 30 June.', fr: '15 par année se terminant le 30 juin.' } },
    { key: 'aic_as', group: 'insurance', source: 'aic_as', label: { en: 'Alberta (AIC) A&S', fr: 'Alberta (AIC) Accident-maladie' },
      cats: [ { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 15 } ],
      note: { en: '15 per year ending 30 June.', fr: '15 par année se terminant le 30 juin.' } },
    { key: 'icobc', group: 'insurance', source: 'icobc', label: { en: 'British Columbia (ICoBC)', fr: 'Colombie-Britannique (ICoBC)' },
      cats: [ { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 15 } ],
      note: { en: '15 per year ending 31 May.', fr: '15 par année se terminant le 31 mai.' } },
    { key: 'icm', group: 'insurance', source: 'icm', label: { en: 'Manitoba (ICM)', fr: 'Manitoba (ICM)' },
      cats: [ { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 15 } ],
      note: { en: '15 per year ending 31 May. A course counts once, so tick the ones you have taken below.', fr: '15 par année se terminant le 31 mai. Un cours ne compte qu\'une fois : cochez ceux déjà suivis ci-dessous.' } },
    { key: 'ics', group: 'insurance', source: 'ics', label: { en: 'Saskatchewan (ICS)', fr: 'Saskatchewan (ICS)' },
      cats: [
        { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 15 },
        { key: 'ethics', label: { en: 'of which ethics', fr: 'dont éthique' }, def: 0 } ],
      note: { en: '15 per licence year. 3 ethics hours within 3 years of licensing and every 5 years after.', fr: '15 par année de permis. 3 heures d\'éthique dans les 3 ans suivant le permis, puis tous les 5 ans.' } },
    { key: 'fsra', group: 'insurance', source: 'institute', label: { en: 'Ontario (FSRA)', fr: 'Ontario (ARSF)' },
      cats: [ { key: 'total', label: { en: 'Hours needed', fr: 'Heures requises' }, def: 30 } ],
      note: { en: '30 hours every two years from your licence date, so the deadline differs for each licensee. Ontario does not accredit courses itself; hours accredited by The Institute count.', fr: '30 heures tous les deux ans à partir de la date de votre permis, donc l\'échéance varie selon la personne. L\'Ontario n\'agrée pas les cours lui-même; les heures reconnues par l\'Institut comptent.' } },
    { key: 'csf', group: 'insurance', source: 'csf', label: { en: 'Quebec (CSF)', fr: 'Québec (CSF)' },
      cats: [
        { key: 'general', label: { en: 'General subjects', fr: 'Matières générales' }, def: 10 },
        { key: 'compliance', label: { en: 'Compliance', fr: 'Conformité' }, def: 10 },
        { key: 'insurance', label: { en: 'Insurance of persons', fr: 'Assurance de personnes' }, def: 0 },
        { key: 'mutual', label: { en: 'Mutual funds', fr: 'Épargne collective' }, def: 0 } ],
      note: { en: 'Per cycle ending 30 Nov 2027: 10 general, 10 compliance, and 10 in each category you are registered in.', fr: 'Par cycle se terminant le 30 nov. 2027 : 10 en matières générales, 10 en conformité et 10 par discipline d\'inscription.' } },
  ];

  const GROUPS = [
    { key: 'designations', label: { en: 'Designations', fr: 'Titres professionnels' } },
    { key: 'investment', label: { en: 'Investment licences', fr: 'Permis en valeurs mobilières' } },
    { key: 'insurance', label: { en: 'Insurance licences', fr: 'Permis en assurance' } },
  ];

  const T = {
    step1: { en: 'Which bodies do you report to?', fr: 'À quels organismes rendez-vous des comptes?' },
    step2: { en: 'How many hours do you still need?', fr: 'Combien d\'heures vous manque-t-il?' },
    step2hint: { en: 'Full-cycle numbers, with the courses marked "This cycle" above already counted. <strong>Earned hours elsewhere? Lower the number by that amount.</strong>', fr: 'Chiffres d\'un cycle complet, les cours marqués « Ce cycle » ci-dessus étant déjà comptés. <strong>Des heures obtenues ailleurs? Réduisez le chiffre d\'autant.</strong>' },
    step3: { en: 'Our courses you\'ve already taken', fr: 'Nos cours déjà suivis' },
    step3hint: { en: 'Courses from this cycle count toward your hours. Courses from an earlier cycle are just left out of the recommendation.', fr: 'Les cours de ce cycle comptent dans vos heures. Ceux d\'un cycle précédent sont simplement exclus de la recommandation.' },
    step3toggle: { en: 'Choose courses', fr: 'Choisir des cours' },
    pick: { en: 'Pick at least one body to see recommendations.', fr: 'Choisissez au moins un organisme pour voir les recommandations.' },
    best: { en: 'Best value', fr: 'Meilleur choix' },
    covers: { en: 'Covers everything', fr: 'Couvre tout' },
    partial: { en: 'Partial', fr: 'Partiel' },
    short: { en: 'short', fr: 'manquant' },
    bundle: { en: 'Bundle', fr: 'Forfait' },
    courses: { en: 'Individual courses', fr: 'Cours individuels' },
    addons: { en: 'plus add-on courses at list price', fr: 'plus des cours à l\'unité au prix courant' },
    inBundle: { en: 'In the bundle', fr: 'Dans le forfait' },
    addOn: { en: 'Add at list price', fr: 'À ajouter au prix courant' },
    taken: { en: 'already taken', fr: 'déjà suivi' },
    takenEarlier: { en: 'counted towards a prior cycle', fr: 'compté dans un cycle précédent' },
    whenLabel: { en: 'When did you take it?', fr: 'Quand l\'avez-vous suivi?' },
    thisCycle: { en: 'This cycle', fr: 'Ce cycle' },
    earlier: { en: 'Earlier', fr: 'Avant' },
    view: { en: 'View bundle', fr: 'Voir le forfait' },
    addCart: { en: 'Add to cart', fr: 'Ajouter au panier' },
    addAll: { en: 'Add all to cart', fr: 'Tout ajouter au panier' },
    adding: { en: 'Adding…', fr: 'Ajout…' },
    showRequest: { en: 'Show the request text', fr: 'Afficher le texte de la demande' },
    copy: { en: 'Copy text', fr: 'Copier le texte' },
    copied: { en: 'Copied', fr: 'Copié' },
    takenWarn: { en: 'includes a course you have taken', fr: 'comprend un cours déjà suivi' },
    nothing: { en: 'No combination of our courses covers all of that.', fr: 'Aucune combinaison de nos cours ne couvre tout cela.' },
    alreadyDone: { en: 'The courses you have taken already cover everything you entered.', fr: 'Les cours déjà suivis couvrent déjà tout ce que vous avez indiqué.' },
    earned: { en: 'already earned', fr: 'déjà acquis' },
    done: { en: 'done', fr: 'faites' },
    left: { en: 'left', fr: 'restantes' },
    allDone: { en: 'done', fr: 'fait' },
    overCycle: { en: 'usual', fr: 'cycle' },
    custom: { en: 'Want something different? Email us and we will put together a custom bundle.', fr: 'Vous voulez autre chose? Écrivez-nous et nous préparerons un forfait sur mesure.' },
    customBtn: { en: 'Request a custom bundle', fr: 'Demander un forfait sur mesure' },
    hours: { en: 'h', fr: 'h' },
    needed: { en: 'needed', fr: 'requis' },
  };

  const money = (n) => '$' + n.toFixed(2);

  // Hours a course contributes toward one body's category.
  function contribution(course, body, cat) {
    const c = course.credits || {};
    const cats = course.cats || {};
    const h = c[body.source] || 0;
    if (!h) return 0;
    switch (body.source) {
      case 'fp':
        if (cat === 'total') return h;
        return (cats.fp === 'Professional Responsibility') === (cat === 'pr') ? h : 0;
      case 'idd':
        return (cats.idd === 'Compliance') === (cat === 'compliance') ? h : 0;
      case 'mfd':
        return ((cats.mfd || '').startsWith('Business Conduct')) === (cat === 'bc') ? h : 0;
      case 'institute':
        return cat === 'ethics' ? (c.institute_ethics || 0) : h;
      case 'ics':
        return cat === 'ethics' ? (c.ics_ethics || 0) : h;
      case 'csf': {
        const map = { General: 'general', Compliance: 'compliance', 'Insurance of Persons': 'insurance', 'Mutual Funds': 'mutual' };
        return map[cats.csf] === cat ? h : 0;
      }
      case 'iqpf':
        return (cats.iqpf === 'SC') === (cat === 'sc') ? h : 0;
      default:
        return h;
    }
  }

  // Provision map for an item: { 'body:cat': hours }
  function provisionOf(item) {
    const out = {};
    if (item.kind === 'product') {
      for (const [k, v] of Object.entries(item.credits || {})) out[k] = v;
      return out;
    }
    for (const body of BODIES) {
      for (const cat of body.cats) {
        const v = contribution(item, body, cat.key);
        if (v) out[`${body.key}:${cat.key}`] = v;
      }
    }
    return out;
  }

  function addProv(target, prov) {
    for (const [k, v] of Object.entries(prov)) target[k] = (target[k] || 0) + v;
  }

  function shortfall(need, prov) {
    let s = 0;
    for (const [k, v] of Object.entries(need)) s += Math.max(0, v - (prov[k] || 0));
    return s;
  }

  class CEFinder extends HTMLElement {
    connectedCallback() {
      const data = JSON.parse(this.querySelector('#ce-finder-data').textContent);
      this.lang = this.dataset.lang === 'fr' ? 'fr' : 'en';
      // routes.root_url is "/" for the default locale and "/fr" (no trailing slash) for French.
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');
      this.allowCourses = this.dataset.allowCourses !== 'false';
      this.maxResults = parseInt(this.dataset.maxResults || '3', 10);
      this.courses = data.courses.filter((c) => c.lang === this.lang);
      // Rebolt bundles only exist in English; CSF product bundles exist in both languages.
      this.bundles = data.bundles.filter((b) => b.lang === this.lang || (b.kind === 'rebolt' && this.lang === 'en'));
      this.byHandle = Object.fromEntries(this.courses.map((c) => [c.handle, c]));
      for (const c of this.courses) c.prov = provisionOf(c);
      for (const b of this.bundles) if (b.kind === 'product') b.prov = provisionOf(b);

      this.selected = new Set();
      this.needs = {};
      // handle -> 'current' (counts toward this cycle) or 'previous' (just not recommended again)
      this.taken = new Map();
      this.utm = [...new URLSearchParams(location.search)].filter(([k]) => k.startsWith('utm_'));

      this.renderShell();
      this.prefillFromUrl();
      this.update();
    }

    t(key) { return T[key][this.lang]; }
    l(obj) { return obj[this.lang]; }

    prefillFromUrl() {
      const params = new URLSearchParams(location.search);
      const q = params.get('licence') || params.get('license');
      if (q) {
        for (const k of q.split(',')) {
          const body = BODIES.find((b) => b.key === k.trim().toLowerCase());
          if (body) this.toggleBody(body.key, true);
        }
      }
      for (const [param, when] of [['taken', 'current'], ['prior', 'previous']]) {
        const list = params.get(param);
        if (!list) continue;
        for (const h of list.split(',')) if (this.byHandle[h.trim()]) this.taken.set(h.trim(), when);
      }
      if (this.taken.size) this.renderTaken();
    }

    // Keep bodies and taken courses in the address bar so any state can be shared.
    syncUrl() {
      const params = new URLSearchParams(location.search);
      params.delete('license');
      if (this.selected.size) params.set('licence', [...this.selected].join(',')); else params.delete('licence');
      const current = [...this.taken].filter(([, w]) => w === 'current').map(([h]) => h);
      const previous = [...this.taken].filter(([, w]) => w === 'previous').map(([h]) => h);
      if (current.length) params.set('taken', current.join(',')); else params.delete('taken');
      if (previous.length) params.set('prior', previous.join(',')); else params.delete('prior');
      const qs = params.toString();
      history.replaceState(null, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    }

    renderShell() {
      this.innerHTML = `
        <form class="ce-finder__form" novalidate>
          <section class="ce-finder__step">
            <h3 class="ce-finder__step-title"><span class="ce-finder__step-num">1</span>${this.t('step1')}</h3>
            ${GROUPS.map((g) => `
              <div class="ce-finder__group">
                <span class="ce-finder__group-label">${this.l(g.label)}</span>
                <div class="ce-finder__chips">
                  ${BODIES.filter((b) => b.group === g.key).map((b) => `
                    <label class="ce-finder__chip"><input type="checkbox" name="body" value="${b.key}">${this.l(b.label)}</label>`).join('')}
                </div>
              </div>`).join('')}
          </section>
          <section class="ce-finder__step" data-step="taken" hidden>
            <h3 class="ce-finder__step-title"><span class="ce-finder__step-num">2</span>${this.t('step3')}</h3>
            <p class="ce-finder__hint">${this.t('step3hint')}</p>
            <details>
              <summary>${this.t('step3toggle')}</summary>
              <div class="ce-finder__taken"></div>
            </details>
          </section>
          <section class="ce-finder__step" data-step="needs" hidden>
            <h3 class="ce-finder__step-title"><span class="ce-finder__step-num">3</span>${this.t('step2')}</h3>
            <p class="ce-finder__hint">${this.t('step2hint')}</p>
            <div class="ce-finder__needs"></div>
          </section>
        </form>
        <div class="ce-finder__results" aria-live="polite"></div>`;

      this.form = this.querySelector('form');
      this.needsEl = this.querySelector('.ce-finder__needs');
      this.takenEl = this.querySelector('.ce-finder__taken');
      this.resultsEl = this.querySelector('.ce-finder__results');

      this.form.addEventListener('change', (e) => {
        const el = e.target;
        if (el.name === 'body') this.toggleBody(el.value, el.checked);
        else if (el.name.startsWith('need:')) { const [, body, cat] = el.name.split(':'); this.needs[body][cat] = Math.max(0, parseFloat(el.value) || 0); }
        this.update();
      });
      this.form.addEventListener('input', (e) => {
        if (e.target.name && e.target.name.startsWith('need:')) {
          const [, body, cat] = e.target.name.split(':');
          this.needs[body][cat] = Math.max(0, parseFloat(e.target.value) || 0);
          this.update();
        }
      });
      this.form.addEventListener('click', (e) => {
        const step = e.target.closest('button[data-step]');
        if (step) {
          const input = step.parentElement.querySelector('input');
          input.value = Math.max(0, (parseFloat(input.value) || 0) + parseInt(step.dataset.step, 10));
          input.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
        // "This cycle" / "Earlier" selector: click the active one again to clear it.
        const when = e.target.closest('button[data-when]');
        if (when) {
          const item = when.closest('.ce-finder__taken-item');
          const handle = item.dataset.handle;
          const active = when.getAttribute('aria-pressed') === 'true';
          if (active) this.taken.delete(handle); else this.taken.set(handle, when.dataset.when);
          for (const b of item.querySelectorAll('button[data-when]')) b.setAttribute('aria-pressed', String(!active && b === when));
          item.classList.toggle('is-set', !active);
          this.update();
        }
      });
      this.resultsEl.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-add]');
        if (btn) { e.preventDefault(); this.addToCart(btn); return; }
        if (e.target.closest('[data-toggle-request]')) {
          const panel = this.resultsEl.querySelector('.ce-finder__request');
          panel.hidden = !panel.hidden;
          return;
        }
        const copy = e.target.closest('[data-copy-request]');
        if (copy) {
          const text = this.resultsEl.querySelector('.ce-finder__request textarea').value;
          try { await navigator.clipboard.writeText(text); copy.textContent = this.t('copied'); }
          catch (err) { this.resultsEl.querySelector('.ce-finder__request textarea').select(); }
        }
      });
      // Hovering a course tile or a group heading highlights its share of each coverage bar.
      this.resultsEl.addEventListener('mouseover', (e) => {
        const src = e.target.closest('.ce-finder__course[data-item], .ce-finder__group-head[data-group]');
        if (!src) return;
        const card = src.closest('.ce-finder__card');
        const byItem = src.classList.contains('ce-finder__course');
        const match = (seg) => byItem ? seg.dataset.item === src.dataset.item : seg.dataset.group === src.dataset.group;
        card.classList.add('is-hovering');
        for (const cov of card.querySelectorAll('.ce-finder__cov')) {
          let hours = 0;
          let beyondCap = false;
          for (const seg of cov.querySelectorAll('.ce-finder__seg')) {
            const on = match(seg);
            seg.classList.toggle('is-hl', on);
            if (on) {
              hours += parseFloat(seg.dataset.hours) || 0;
              if (parseFloat(seg.dataset.shown) < parseFloat(seg.dataset.hours)) beyondCap = true;
            }
          }
          const cap = cov.querySelector('.ce-finder__cap');
          if (cap) cap.classList.toggle('is-hl', beyondCap);
          cov.querySelector('.ce-finder__cov-hl').textContent = hours ? `+${hours}${this.t('hours')} · ` : '';
        }
      });
      this.resultsEl.addEventListener('mouseout', (e) => {
        const src = e.target.closest('.ce-finder__course[data-item], .ce-finder__group-head[data-group]');
        if (!src || src.contains(e.relatedTarget)) return;
        const card = src.closest('.ce-finder__card');
        card.classList.remove('is-hovering');
        for (const seg of card.querySelectorAll('.ce-finder__seg.is-hl, .ce-finder__cap.is-hl')) seg.classList.remove('is-hl');
        for (const hl of card.querySelectorAll('.ce-finder__cov-hl')) hl.textContent = '';
      });
    }

    toggleBody(key, on) {
      const input = this.form.querySelector(`input[name="body"][value="${key}"]`);
      if (input) input.checked = on;
      if (on) {
        this.selected.add(key);
        if (!this.needs[key]) {
          const body = BODIES.find((b) => b.key === key);
          this.needs[key] = Object.fromEntries(body.cats.map((c) => [c.key, c.def]));
        }
      } else {
        this.selected.delete(key);
      }
      this.renderNeeds();
    }

    renderNeeds() {
      const bodies = BODIES.filter((b) => this.selected.has(b.key));
      this.querySelector('[data-step="needs"]').hidden = bodies.length === 0;
      this.querySelector('[data-step="taken"]').hidden = bodies.length === 0;
      this.needsEl.innerHTML = bodies.map((b) => `
        <div class="ce-finder__need">
          <div class="ce-finder__need-head">
            <span class="ce-finder__need-title">${this.l(b.label)}</span>
            <span class="ce-finder__need-note">${this.l(b.note)}</span>
          </div>
          ${b.cats.map((c) => `
            <div class="ce-finder__req" data-note="${b.key}:${c.key}">
              <label class="ce-finder__req-label" for="need-${b.key}-${c.key}">${this.l(c.label)}</label>
              <div class="ce-finder__num">
                <button type="button" data-step="-1" aria-label="${this.l({ en: 'One hour less', fr: 'Une heure de moins' })}">&minus;</button>
                <input id="need-${b.key}-${c.key}" name="need:${b.key}:${c.key}" type="number" inputmode="decimal" min="0" step="0.5" value="${this.needs[b.key][c.key]}">
                <button type="button" data-step="1" aria-label="${this.l({ en: 'One hour more', fr: 'Une heure de plus' })}">+</button>
              </div>
              <div class="ce-finder__req-progress">
                <span class="ce-finder__req-bar"><span></span></span>
                <span class="ce-finder__req-text"></span>
              </div>
            </div>`).join('')}
        </div>`).join('');
      this.renderTaken();
      this.refreshNeedNotes();
    }

    // Beside each hours field: a small bar of what the this-cycle courses already cover, and what is left.
    refreshNeedNotes() {
      const earned = this.earnedProv();
      for (const row of this.querySelectorAll('.ce-finder__req')) {
        const [body, cat] = row.dataset.note.split(':');
        const need = (this.needs[body] || {})[cat] || 0;
        const done = Math.min(need, earned[`${body}:${cat}`] || 0);
        const left = need - done;
        row.querySelector('.ce-finder__req-bar > span').style.width = need ? `${(done / need) * 100}%` : '0%';
        row.classList.toggle('is-done', need > 0 && left === 0);
        // Above the body's own full-cycle figure: flag it, since that is more than one cycle asks for.
        const def = (BODIES.find((b) => b.key === body).cats.find((c) => c.key === cat) || {}).def || 0;
        const over = def > 0 && need > def;
        row.classList.toggle('is-over', over);
        row.classList.toggle('is-at-cap', def > 0 && need >= def);
        row.querySelector('.ce-finder__req-text').textContent = !need
          ? ''
          : (done > 0
            ? (left === 0 ? this.t('allDone') : `${done} ${this.t('done')} · ${left} ${this.t('left')}`)
            : `${need} ${this.t('left')}`) + (over ? ` · ${this.t('overCycle')} ${def}${this.t('hours')}` : '');
      }
    }

    // Step 3 lists only the courses that earn credit with at least one selected body,
    // keeps whatever is already ticked, and links each course to its page.
    renderTaken() {
      const relevant = this.courses
        .filter((c) => Object.keys(c.prov).some((k) => this.selected.has(k.split(':')[0])))
        .sort((a, b) => a.title.localeCompare(b.title, this.lang));
      this.takenEl.innerHTML = relevant.map((c) => `
        <div class="ce-finder__taken-item ${this.taken.has(c.handle) ? 'is-set' : ''}" data-handle="${c.handle}">
          <a class="ce-finder__taken-title" href="${this.productUrl(c.handle)}" target="_blank" rel="noopener">${c.title}</a>
          <div class="ce-finder__when" role="group" aria-label="${c.title}: ${this.t('whenLabel')}">
            <button type="button" data-when="current" aria-pressed="${this.taken.get(c.handle) === 'current'}">${this.t('thisCycle')}</button>
            <button type="button" data-when="previous" aria-pressed="${this.taken.get(c.handle) === 'previous'}">${this.t('earlier')}</button>
          </div>
        </div>`).join('');
    }

    needVector() {
      const need = {};
      for (const key of this.selected) {
        for (const [cat, v] of Object.entries(this.needs[key])) if (v > 0) need[`${key}:${cat}`] = v;
      }
      return need;
    }

    // Greedy fill: repeatedly add the course that removes the most shortfall per dollar.
    fill(need, prov, used, price) {
      const items = [];
      let current = shortfall(need, prov);
      while (current > 0) {
        let best = null;
        for (const c of this.courses) {
          if (used.has(c.handle) || this.taken.has(c.handle)) continue;
          const trial = { ...prov };
          addProv(trial, c.prov);
          const gain = current - shortfall(need, trial);
          if (gain <= 0) continue;
          const score = gain / c.price;
          if (!best || score > best.score) best = { course: c, score, trial, after: current - gain };
        }
        if (!best) break;
        items.push(best.course);
        used.add(best.course.handle);
        prov = best.trial;
        price += best.course.price;
        current = best.after;
      }
      return { items, prov, price, short: current };
    }

    // Hours already earned from the courses ticked in step 3.
    earnedProv() {
      const prov = {};
      for (const [h, when] of this.taken) {
        const c = this.byHandle[h];
        if (c && when === 'current') addProv(prov, c.prov);
      }
      return prov;
    }

    static bundleAvailable(bundle, now = Date.now()) {
      if (bundle.kind !== 'rebolt') return true;
      const { starts_at: start, ends_at: end } = bundle;
      return (start == null || (Number.isFinite(start) && now >= start)) &&
        (end == null || (Number.isFinite(end) && now < end));
    }

    recommend() {
      const need = this.needVector();
      if (!Object.keys(need).length) return [];
      const earned = this.earnedProv();
      const options = [];

      // One option per bundle, plus one per pair of bundles with no course in common,
      // so a general bundle and a compliance bundle can be bought together.
      const buildOption = (bundles) => {
        const used = new Set();
        const prov = { ...earned };
        let takenCount = 0;
        let price = 0;
        for (const b of bundles) {
          price += b.price;
          if (b.kind === 'rebolt') {
            for (const h of b.courses) {
              used.add(h);
              const c = this.byHandle[h];
              if (!c) continue;
              if (this.taken.has(h)) { takenCount++; continue; }
              addProv(prov, c.prov);
            }
          } else {
            addProv(prov, b.prov);
          }
        }
        const base = shortfall(need, prov);
        const filled = this.allowCourses ? this.fill(need, prov, used, price) : { items: [], prov, price, short: base };
        return { kind: 'bundle', bundles, addons: filled.items, prov: filled.prov, price: filled.price, short: filled.short, takenCount };
      };
      const courseSet = (b) => new Set(b.kind === 'rebolt' ? b.courses : []);
      const disjoint = (a, b) => [...courseSet(a)].every((h) => !courseSet(b).has(h));

      const now = Date.now();
      const bundles = this.bundles.filter((b) => CEFinder.bundleAvailable(b, now));
      for (let i = 0; i < bundles.length; i++) {
        options.push(buildOption([bundles[i]]));
        for (let j = i + 1; j < bundles.length; j++) {
          const a = bundles[i], b = bundles[j];
          if (a.kind === 'product' && b.kind === 'product') continue;
          if (disjoint(a, b)) options.push(buildOption([a, b]));
        }
      }

      if (this.allowCourses) {
        const filled = this.fill(need, { ...earned }, new Set(), 0);
        if (filled.items.length) options.push({ kind: 'courses', bundles: [], addons: filled.items, prov: filled.prov, price: filled.price, short: filled.short, takenCount: 0 });
      }

      options.sort((a, b) => (a.short === 0) !== (b.short === 0) ? (a.short === 0 ? -1 : 1) : a.short !== b.short && a.short > 0 ? a.short - b.short : a.price - b.price);
      // Drop options whose item set duplicates a cheaper one already listed.
      const seen = new Set();
      return options.filter((o) => {
        const key = [...o.bundles.map((b) => (b.kind === 'rebolt' ? b.id : b.handle)), ...o.addons.map((c) => c.handle)].sort().join('|');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, this.maxResults);
    }

    update() {
      this.syncUrl();
      this.refreshNeedNotes();
      const need = this.needVector();
      if (!this.selected.size) {
        this.resultsEl.innerHTML = `<div class="ce-finder__empty">${this.t('pick')}</div>`;
        return;
      }
      if (shortfall(need, this.earnedProv()) === 0) {
        this.resultsEl.innerHTML = `<div class="ce-finder__empty">${this.t('alreadyDone')}</div>`;
        return;
      }
      const options = this.recommend();
      const cards = options.map((o, i) => this.renderOption(o, need, i === 0 && o.short === 0)).join('');
      this.resultsEl.innerHTML = (cards || `<div class="ce-finder__empty">${this.t('nothing')}</div>`) + this.renderFallback(need);
    }

    link(path) {
      const url = new URL(path, location.origin);
      for (const [k, v] of this.utm) url.searchParams.set(k, v);
      return url.pathname + url.search + url.hash;
    }

    productUrl(handle) { return this.link(`${this.root}products/${handle}`); }

    renderOption(o, need, best) {
      const bundles = o.bundles;
      const b = bundles[0] || null;
      const title = bundles.length ? bundles.map((x) => x.name).join(' + ') : this.t('courses');
      const listPrice = bundles.reduce((s, x) => s + (x.separate_price || x.price), 0) + o.addons.reduce((s, c) => s + c.price, 0);
      const thumb = (src, alt) => src
        ? `<img class="ce-finder__thumb" src="${src}${src.includes('?') ? '&' : '?'}width=320" width="160" height="120" loading="lazy" alt="${alt.replace(/"/g, '&quot;')}">`
        : '<span class="ce-finder__thumb"></span>';
      const row = (opts) => `<li class="ce-finder__course ${opts.cls || ''}" data-item="${opts.item || ''}" data-group="${opts.group || ''}">
          ${thumb(opts.image, opts.title)}
          <span class="ce-finder__course-title">${opts.href ? `<a href="${opts.href}">${opts.title}</a>` : opts.title}</span>
          <span class="ce-finder__course-meta">${opts.meta || ''}</span>
        </li>`;

      // Contributors, in display order, for the hover highlight on the coverage bars.
      // Courses already taken come first as "earned" hours.
      const contributors = [];
      for (const [h, when] of this.taken) {
        const c = this.byHandle[h];
        if (c && when === 'current') contributors.push({ id: h, group: 'earned', prov: c.prov });
      }
      const groups = [];
      bundles.forEach((x, i) => {
        const group = `bundle${i}`;
        let rows = '';
        let count = 0;
        if (x.kind === 'rebolt') {
          count = x.courses.length;
          rows = x.courses.map((h) => {
            const c = this.byHandle[h];
            const taken = this.taken.has(h);
            if (c && !taken) contributors.push({ id: h, group, prov: c.prov });
            const meta = !taken ? '' : this.taken.get(h) === 'previous' ? this.t('takenEarlier') : this.t('taken');
            return row({ image: c && c.image, title: c ? c.title : h, href: this.productUrl(h), cls: taken ? 'is-taken' : '', meta, item: h, group });
          }).join('');
        } else {
          count = x.members.length;
          contributors.push({ id: x.handle, group, prov: x.prov });
          rows = x.members.map((m) => row({ image: x.image, title: m, item: x.handle, group })).join('');
        }
        const discount = x.discount_pct ? `${x.discount_pct}% ${this.l({ en: 'off', fr: 'de rabais' })}` : '';
        const label = bundles.length > 1 ? x.name : this.t('inBundle');
        groups.push(`<div class="ce-finder__group-block">
          <div class="ce-finder__group-head" data-group="${group}"><span>${label}</span><span>${count} ${this.l({ en: 'courses', fr: 'cours' })}${discount ? ' · ' + discount : ''} · ${money(x.price)}</span></div>
          <ul class="ce-finder__items">${rows}</ul>
        </div>`);
      });
      for (const c of o.addons) contributors.push({ id: c.handle, group: 'addon', prov: c.prov });
      const addonRows = o.addons.map((c) => row({ image: c.image, title: c.title, href: this.productUrl(c.handle), meta: money(c.price), item: c.handle, group: 'addon' })).join('');
      const addonTotal = o.addons.reduce((s, c) => s + c.price, 0);
      if (o.addons.length) {
        groups.push(`<div class="ce-finder__group-block ce-finder__group-block--addons">
          <div class="ce-finder__group-head" data-group="addon"><span>${b ? this.t('addOn') : this.t('courses')}</span><span>${o.addons.length} ${this.l({ en: 'courses', fr: 'cours' })} · ${money(addonTotal)}</span></div>
          <ul class="ce-finder__items">${addonRows}</ul>
        </div>`);
      }

      const coverage = Object.entries(need).map(([k, v]) => {
        const [bodyKey, catKey] = k.split(':');
        const body = BODIES.find((x) => x.key === bodyKey);
        const cat = body.cats.find((x) => x.key === catKey);
        const have = o.prov[k] || 0;
        const short = have < v;
        const earnedHours = contributors.filter((it) => it.group === 'earned').reduce((s, it) => s + (it.prov[k] || 0), 0);
        // One segment per contributor, in display order, until the bar is full.
        let remaining = v;
        const segs = contributors.map((it) => {
          const hours = it.prov[k] || 0;
          const shown = Math.max(0, Math.min(hours, remaining));
          remaining -= shown;
          return hours ? `<span class="ce-finder__seg" data-item="${it.id}" data-group="${it.group}" data-hours="${hours}" data-shown="${shown}" style="width:${(shown / v) * 100}%"></span>` : '';
        }).join('') + (have > v ? '<span class="ce-finder__cap" title="' + this.l({ en: 'More hours than needed', fr: 'Plus d\'heures que nécessaire' }) + '"></span>' : '');
        return `<div class="ce-finder__cov ${short ? 'is-short' : ''}" data-need="${k}">
          <span>${this.l(body.label)} · ${this.l(cat.label)}</span>
          <span class="ce-finder__cov-val"><span class="ce-finder__cov-hl"></span>${have}${this.t('hours')} / ${v}${this.t('hours')}${earnedHours ? ` <span class="ce-finder__cov-earned">· ${Math.min(earnedHours, v)}${this.t('hours')} ${this.t('earned')}</span>` : ''}${short ? ` · ${v - have} ${this.t('short')}` : ''}</span>
          <span class="ce-finder__cov-bar">${segs}</span>
        </div>`;
      }).join('');

      // Rebolt bundles go through the cart transform (data-bundle); product bundles and
      // add-on courses are plain variants (data-add).
      const rebolt = bundles.filter((x) => x.kind === 'rebolt');
      const ids = [...bundles.filter((x) => x.kind === 'product').map((x) => x.variant_id), ...o.addons.map((c) => c.variant_id)].filter(Boolean);
      const many = bundles.length + o.addons.length > 1;
      let cta = `<button type="button" class="button" data-add="${ids.join(',')}" data-bundle="${rebolt.map((x) => x.id).join(',')}">${many ? this.t('addAll') : this.t('addCart')}</button>`;
      for (const x of rebolt.filter((bundle) => bundle.page_listed !== false)) {
        cta += ` <a class="link" href="${this.link(`${this.dataset.bundlesUrl}#${x.anchor}`)}">${this.t('view')}${rebolt.length > 1 ? `: ${x.name}` : ''}</a>`;
      }

      const badge = o.short === 0
        ? `<span class="ce-finder__badge">${best ? this.t('best') : this.t('covers')}</span>`
        : `<span class="ce-finder__badge ce-finder__badge--warn">${this.t('partial')}</span>`;
      const takenWarn = o.takenCount ? `<span class="ce-finder__badge ce-finder__badge--warn">${this.t('takenWarn')}</span>` : '';
      const estimated = bundles.some((x) => x.estimated) ? `<span class="ce-finder__badge ce-finder__badge--warn">${this.l({ en: 'credits to be confirmed', fr: 'UFC à confirmer' })}</span>` : '';

      return `<article class="ce-finder__card ${best ? 'ce-finder__card--best' : ''}">
        <div class="ce-finder__pin">
        <div class="ce-finder__card-head">
          <div>${badge} ${takenWarn} ${estimated}<h4 class="ce-finder__card-title">${title}</h4>
            ${o.addons.length && b ? `<div class="ce-finder__note">${this.t('addons')}</div>` : ''}</div>
          <div class="ce-finder__price"><span class="ce-finder__price-now">${money(o.price)}</span>${listPrice > o.price + 0.01 ? `<span class="ce-finder__price-was">${money(listPrice)}</span>` : ''}</div>
        </div>
        <div class="ce-finder__coverage">${coverage}</div>
        </div>
        <div class="ce-finder__scroll">${groups.join('')}</div>
        <div class="ce-finder__actions">${cta}</div>
      </article>`;
    }

    renderFallback(need) {
      const lines = Object.entries(need).map(([k, v]) => {
        const [bodyKey, catKey] = k.split(':');
        const body = BODIES.find((x) => x.key === bodyKey);
        const cat = body.cats.find((x) => x.key === catKey);
        return `${body.label.en} - ${cat.label.en}: ${v} hours`;
      });
      const takenTitles = [...this.taken.keys()].map((h) => (this.byHandle[h] || {}).title || h);
      const body = [`I need the following CE credits:`, ...lines, '', takenTitles.length ? `Already taken: ${takenTitles.join(', ')}` : '', '', 'Could you suggest a custom bundle?'].join('\n');
      const href = `mailto:${this.dataset.contact}?subject=${encodeURIComponent('Custom bundle request')}&body=${encodeURIComponent(body)}`;
      // The mailto link needs a configured mail app, so the request is also shown in a panel
      // with the address and a copy button.
      const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;');
      return `<div class="ce-finder__fallback">
        <p>${this.t('custom')}</p>
        <div class="ce-finder__fallback-actions">
          <a class="button button--outline" href="${href}">${this.t('customBtn')}</a>
          <button type="button" class="link" data-toggle-request>${this.t('showRequest')}</button>
        </div>
        <div class="ce-finder__request" hidden>
          <p>${this.l({ en: 'Email this to', fr: 'Envoyez ceci à' })} <a href="mailto:${this.dataset.contact}">${this.dataset.contact}</a></p>
          <textarea readonly rows="8">${esc(body)}</textarea>
          <button type="button" class="button button--outline" data-copy-request>${this.t('copy')}</button>
        </div>
      </div>`;
    }

    // Rebolt (Bristm) bundles are assembled by a Shopify cart transform: the component lines
    // share a _th_bundle_uid property and the cart carries an rb_sh_box attribute describing
    // the bundle, which the app's discount function reads. Sending the same shape ourselves
    // produces the merged, discounted bundle line without the app's script. Verified 3 Nov 2026.
    bundleRequest(bundle) {
      const uid = `shproduct_${bundle.id}_${Math.random().toString(36).slice(2, 20)}`;
      const courses = bundle.courses.map((h) => this.byHandle[h]).filter(Boolean);
      const box = {
        bt: bundle.name, isb: '1', fp: '0', ct: 1, mq: null, dtype: '0', dval: bundle.discount_pct,
        cv: courses.map((c) => ({ product_id: String(c.product_id), variant_id: c.variant_id, quantity: 1, price: c.price, hdl: c.handle })),
      };
      return { uid, box, items: courses.map((c) => ({ id: c.variant_id, quantity: 1, properties: { _th_bundle_uid: uid } })) };
    }

    // The app's own script watches the page's cart traffic and reconciles it, pruning
    // descriptors that have no lines yet and lines whose descriptor it does not know,
    // which races with our requests. Running the sequence in a worker keeps it out of the
    // app's sight; same-origin cookies still apply, so it is the shopper's cart.
    static cartSequenceSource() {
      return `async function run({ root, boxes, batches }) {
        const J = { 'Content-Type': 'application/json', Accept: 'application/json' };
        const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
        let existing = {};
        try { existing = JSON.parse((cart.attributes || {}).rb_sh_box || '{}'); } catch (err) { existing = {}; }
        Object.assign(existing, boxes);
        if (Object.keys(boxes).length) {
          const u = await fetch(root + 'cart/update.js', { method: 'POST', headers: J, body: JSON.stringify({ attributes: { rb_sh_box: JSON.stringify(existing) } }) });
          if (!u.ok) throw new Error(await u.text());
        }
        const add = async (items) => {
          const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: J, body: JSON.stringify({ items }) });
          if (!r.ok) throw new Error(await r.text());
        };
        for (const items of batches) if (items.length) await add(items);
        // Verify and repair: the app's script may have pruned descriptors or lines meanwhile.
        for (let attempt = 0; attempt < 4; attempt++) {
          const now = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
          let attr = {};
          try { attr = JSON.parse((now.attributes || {}).rb_sh_box || '{}'); } catch (err) { attr = {}; }
          const uidsInCart = new Set(now.items.map((i) => (i.properties || {})._th_bundle_uid).filter(Boolean));
          const missingAttr = Object.keys(boxes).filter((uid) => !attr[uid]);
          const missingLines = batches.filter((items) => items.length && items[0].properties && !uidsInCart.has(items[0].properties._th_bundle_uid));
          if (!missingAttr.length && !missingLines.length) break;
          if (missingAttr.length) {
            Object.assign(attr, boxes);
            const u = await fetch(root + 'cart/update.js', { method: 'POST', headers: J, body: JSON.stringify({ attributes: { rb_sh_box: JSON.stringify(attr) } }) });
            if (!u.ok) throw new Error(await u.text());
          }
          for (const items of missingLines) await add(items);
        }
      }`;
    }

    runCartSequence(payload) {
      const src = CEFinder.cartSequenceSource();
      return new Promise((resolve, reject) => {
        let worker;
        try {
          const code = `${src}\nself.onmessage = (e) => run(e.data).then(() => self.postMessage({ ok: true }), (err) => self.postMessage({ ok: false, error: String(err) }));`;
          worker = new Worker(URL.createObjectURL(new Blob([code], { type: 'text/javascript' })));
        } catch (err) {
          worker = null;
        }
        if (!worker) {
          // No worker available: run on the main thread and hope the app's script stays quiet.
          new Function('return ' + src)()(payload).then(resolve, reject);
          return;
        }
        worker.onmessage = (e) => { worker.terminate(); e.data.ok ? resolve() : reject(new Error(e.data.error)); };
        worker.onerror = () => { worker.terminate(); new Function('return ' + src)()(payload).then(resolve, reject); };
        worker.postMessage(payload);
      });
    }

    async addToCart(btn) {
      const ids = (btn.dataset.add || '').split(',').map((id) => parseInt(id, 10)).filter(Boolean);
      const bundleIds = (btn.dataset.bundle || '').split(',').filter(Boolean);
      const rebolt = bundleIds.map((id) => this.bundles.find((b) => b.kind === 'rebolt' && b.id === id)).filter(Boolean);
      // A result may have expired while the tab was open. Never add only its add-ons.
      if (rebolt.length !== bundleIds.length || rebolt.some((b) => !CEFinder.bundleAvailable(b))) {
        this.update();
        return;
      }
      if (!ids.length && !rebolt.length) return;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = this.t('adding');
      try {
        const boxes = {};
        const batches = [];
        for (const bundle of rebolt) {
          const req = this.bundleRequest(bundle);
          boxes[req.uid] = req.box;
          batches.push(req.items);
        }
        if (ids.length) batches.push(ids.map((id) => ({ id, quantity: 1 })));
        await this.runCartSequence({ root: new URL(this.root, location.origin).href, boxes, batches });
        location.href = `${this.root}cart`;
      } catch (err) {
        console.error('ce-finder: add to cart failed', err);
        btn.disabled = false;
        btn.textContent = label;
      }
    }
  }

  if (!customElements.get('ce-finder')) customElements.define('ce-finder', CEFinder);
})();
